import type { ReactNode } from "react";
import { useLocale } from "@/i18n/locale-context";
import type { Locale } from "@/i18n/types";

export type TipItemData = {
  title: string;
  detail: string;
  category?: string;
  horizon?: string;
  frequency?: string;
  disclaimer?: string;
};

const CATEGORY_LABEL: Record<Locale, Record<string, string>> = {
  fr: { habit: "Habitude", nutrition: "Nutrition", supplement: "Supplément", posture: "Posture", sleep: "Sommeil", skincare: "Soin de la peau", hydration: "Hydratation", exercise: "Exercice", mindset: "Mental" },
  en: { habit: "Habit", nutrition: "Nutrition", supplement: "Supplement", posture: "Posture", sleep: "Sleep", skincare: "Skincare", hydration: "Hydration", exercise: "Exercise", mindset: "Mindset" },
  nl: { habit: "Gewoonte", nutrition: "Voeding", supplement: "Supplement", posture: "Houding", sleep: "Slaap", skincare: "Huidverzorging", hydration: "Hydratatie", exercise: "Oefening", mindset: "Mindset" },
};

const HORIZON_LABEL: Record<Locale, Record<string, string>> = {
  fr: { daily: "Quotidien", weekly: "Hebdo", "4-weeks": "4 semaines", "12-weeks": "12 semaines" },
  en: { daily: "Daily", weekly: "Weekly", "4-weeks": "4 weeks", "12-weeks": "12 weeks" },
  nl: { daily: "Dagelijks", weekly: "Wekelijks", "4-weeks": "4 weken", "12-weeks": "12 weken" },
};

export function TipItem({
  index,
  tip,
  numberClassName,
  detailClassName,
  badgeClassName,
  disclaimerClassName,
}: {
  index: number;
  tip: TipItemData;
  numberClassName: string;
  detailClassName: string;
  badgeClassName: string;
  disclaimerClassName: string;
}): ReactNode {
  const { locale } = useLocale();
  const cat = tip.category ? CATEGORY_LABEL[locale][tip.category] ?? tip.category : null;
  const hor = tip.horizon ? HORIZON_LABEL[locale][tip.horizon] ?? tip.horizon : null;
  return (
    <li className="flex gap-4">
      <span className={numberClassName} style={{ fontFamily: "var(--font-display)" }}>
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="font-semibold text-sm">{tip.title}</p>
          {cat && <span className={badgeClassName}>{cat}</span>}
          {hor && <span className={badgeClassName}>{hor}</span>}
          {tip.frequency && <span className={badgeClassName}>{tip.frequency}</span>}
        </div>
        <p className={detailClassName}>{tip.detail}</p>
        {tip.disclaimer && (
          <p className={disclaimerClassName}>⚠ {tip.disclaimer}</p>
        )}
      </div>
    </li>
  );
}